import React, { useState } from 'react';
import styled from '@emotion/native';
import { useMutation, useQuery } from '@apollo/client';
import { H3, BodyText } from '@/shared/components/StyledText';
import { sand, white, subheadline, green } from '@/shared/styles/colors';
import ScrollableView from '@/shared/components/ScrollableView';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import Spacer from '@/shared/components/Spacer';
import ClientInfoBox from '@/components/client-notes/ClientInfoBox';
import CreateNoteForm from '@/components/client-notes/CreateNoteForm';
import NotesList from '@/components/client-notes/NotesList';
import { CREATE_CLIENT_NOTE } from '@/shared/mutations';
import {
  MutationResult,
  MutationCreateClientNoteArgs,
  QueryCheckClientHasConsultationArgs,
  QueryResult,
} from '@/shared/types/generated';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import {
  CHECK_CLIENT_HAS_CONSULTATION,
  GET_CLIENT_NOTES,
} from '@/shared/queries';
import { router } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import BackButton from '@/shared/components/BackButton';

interface ClientNotesProps {
  clientId: number;
}

const ClientNotes: React.FC<ClientNotesProps> = ({ clientId }) => {
  const { isDesktop } = useBreakpoint();
  const [isNoteFormVisible, setIsNoteFormVisible] = useState(false);
  const { setSnackbarState } = useStore();

  const [createClientNote, { loading: isCreatingNote }] = useMutation<
    MutationResult<'createClientNote'>,
    MutationCreateClientNoteArgs
  >(CREATE_CLIENT_NOTE, {
    refetchQueries: () => [
      {
        query: GET_CLIENT_NOTES,
        variables: { input: { clientId } },
      },
    ],
  });

  useQuery<
    QueryResult<'checkClientHasConsultation'>,
    QueryCheckClientHasConsultationArgs
  >(CHECK_CLIENT_HAS_CONSULTATION, {
    variables: { input: { clientId } },
    onCompleted: (data) => {
      // Make sure stylist has consultations with this client
      const hasConsultation = data.checkClientHasConsultation?.hasConsultation;
      if (!hasConsultation) {
        router.replace(ROUTES.SESSIONS.path);
      }
    },
    onError: (error) => {
      SentryService.captureError(
        'Error in client notes consultation check:',
        error,
      );
    },
  });

  const handleToggleNoteForm = () => {
    setIsNoteFormVisible(!isNoteFormVisible);
  };

  const handleSaveNote = async (content: string) => {
    try {
      await createClientNote({
        variables: {
          input: {
            clientId,
            content,
          },
        },
      });

      setSnackbarState({
        open: true,
        message: 'Note saved successfully!',
        severity: 'success',
      });
      setIsNoteFormVisible(false);
    } catch (error) {
      SentryService.captureError('Error creating client note:', error);
      setSnackbarState({
        open: true,
        message: 'Failed to save note. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCancelNote = () => {
    setIsNoteFormVisible(false);
  };

  return (
    <ScrollableView
      contentContainerStyle={{
        alignItems: 'center',
        flexGrow: 1,
      }}
      scrollEnabled
    >
      <Container isDesktop={isDesktop}>
        <ContentContainer hasNoteForm={isNoteFormVisible}>
          <BackButton
            tintColor={green}
            containerStyle={{ alignSelf: 'flex-start', marginBottom: 10 }}
          />
          <HeaderSection isDesktop={isDesktop}>
            {!isDesktop && (
              <H3 style={{ textAlign: 'center', marginBottom: 8 }}>
                PRIVATE NOTES
              </H3>
            )}
            <SubtitleText isDesktop={isDesktop}>
              Keep track privately of client preferences, session takeaways, and
              ideas
            </SubtitleText>
          </HeaderSection>

          <ClientInfoBox
            clientId={clientId}
            onToggleNoteForm={handleToggleNoteForm}
          />

          <CreateNoteForm
            isVisible={isNoteFormVisible}
            onSaveNote={handleSaveNote}
            onCancel={handleCancelNote}
            isLoading={isCreatingNote}
          />

          {isNoteFormVisible && <Spacer height={24} />}

          <NotesList clientId={clientId} />
        </ContentContainer>
      </Container>
    </ScrollableView>
  );
};

export default ClientNotes;

const Container = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  marginHorizontal: isDesktop ? 30 : 0,
  paddingTop: isDesktop ? 0 : 20,
  backgroundColor: isDesktop ? white : sand,
  display: 'flex',
  alignSelf: 'stretch',
  flex: 1,
}));

const ContentContainer = styled.View<{ hasNoteForm?: boolean }>(
  ({ hasNoteForm }) => ({
    alignSelf: 'stretch',
    height: '100%',
    paddingHorizontal: 20,
    paddingBottom: hasNoteForm ? 120 : 20, // Extra padding when form is visible
  }),
);

const HeaderSection = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  alignItems: 'center',
  marginBottom: isDesktop ? 0 : 24,
}));

const SubtitleText = styled(BodyText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    width: '100%',
    maxWidth: isDesktop ? '100%' : 300,
    color: subheadline,
    fontSize: 16,
    textAlign: isDesktop ? 'left' : 'center',
    marginBottom: isDesktop ? 18 : 0,
  }),
);
